import { useKeycloak } from '@react-keycloak/web';
import { create } from 'zustand'
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAllProjects, getProjectById, getProjectRiskCounts, getProjects } from '../api/projects';
import { getProjectDailyUpdates } from '../api/dailyUpdates';
import { getAllProjectActionItems, getProjectActionItems } from '../api/actionItems';
import { getProjectMeetingNotes } from '../api/meetingNotes';
import { ItemListQuery, ProjectListQuery } from '../types/pagination';

/**
 * Creates a store for projects.
 * @returns {ProjectsStore} The store.
 */
export const useProjectsStore = create<ProjectsStore>((set) => {
  return {
    projects: [],
    fullProjects: [],
    listProjects: [],
    listPagination: {
      page: 0,
      size: 0,
      total: 0
    },
    setAllProjects: (projects: ProjectInterface[]) => {
      set((_state) => ({
        projects: [...projects]
      }))
    },
    setListPage: (projectsPageData: ProjectsInterface) => {
      set((_state) => ({
        listPagination: {
          page: projectsPageData.page,
          size: projectsPageData.size,
          total: projectsPageData.total
        },
        listProjects: [
          ...projectsPageData.results
        ]
      }))
    },
    setProjectById: (project: ProjectInterface) => {
      set((state) => {
        return {
          fullProjects: [
            ...state.fullProjects.filter((p) => p.id !== project.id),
            project
          ]
        }
      })
    }
  };
});

// @ts-ignore
export const useProjectDailyUpdateStore = create<ProjectDailyUpdateStore>((set) => {
  return {
    projectDailyUpdates: {},
    pagination: {
      page: 0,
      size: 0,
      total: 0
    },
    setProjectDailyUpdatePage: (project_id: string, projectDailyUpdatePageData: ProjectDailyUpdatesInterface) => {
      set((state): any => ({
          pagination: {
            page: projectDailyUpdatePageData.page,
            size: projectDailyUpdatePageData.size,
            total: projectDailyUpdatePageData.total
          },
          projectDailyUpdates: {
            ...state.projectDailyUpdates,
            [project_id]: projectDailyUpdatePageData.results
          }
        })
      )
    },
    updateSingleDailyUpdate: (project_id: string, dailyUpdate: ProjectDailyUpdate) => {
      set((state): any => ({
        ...state,
        projectDailyUpdates: {
          ...state.projectDailyUpdates,
          [project_id]: state.projectDailyUpdates[project_id]?.map((du: ProjectDailyUpdate) =>
            du.id === dailyUpdate.id ? { ...du, ...dailyUpdate } : du
          ) || []
        }
      }));
    },
  };
});

export const useProjectActionItemsStore = create<ProjectActionItemsStore>((set) => {
  return {
    projectActionItems: {},
    pagination: {
      page: 0,
      size: 0,
      total: 0
    },
    setProjectActionItemsPage: (project_id: string, projectActionItemsPageData: ProjectActionItemsInterface) => {
      set((state): any => ({
          pagination: {
            page: projectActionItemsPageData.page,
            size: projectActionItemsPageData.size,
            total: projectActionItemsPageData.total
          },
          projectActionItems: {
            ...state.projectActionItems,
            [project_id]: projectActionItemsPageData.results
          }
        })
      )
    },
    updateSingleActionItem: (project_id: string, actionItem: ProjectActionItem) => {
      set((state): any => ({
        ...state,
        projectActionItems: {
          ...state.projectActionItems,
          [project_id]: state.projectActionItems[project_id]?.map((ai: ProjectActionItem) =>
            ai.id === actionItem.id ? { ...ai, ...actionItem } : ai
          ) || []
        }
      }));
    },
  };
});

// @ts-ignore
export const useProjectMeetingNotesStore = create<ProjectMeetingNotesStore>((set) => {
  return {
    projectMeetingNotes: {},
    pagination: {
      page: 0,
      size: 0,
      total: 0
    },
    setProjectMeetingNotesPage: (project_id: string, projectMeetingNotesPageData: ProjectMeetingNotesInterface) => {
      set((state): any => ({
          pagination: {
            page: projectMeetingNotesPageData.page,
            size: projectMeetingNotesPageData.size,
            total: projectMeetingNotesPageData.total
          },
          projectMeetingNotes: {
            ...state.projectMeetingNotes,
            [project_id]: projectMeetingNotesPageData.results
          }
        })
      )
    },
    updateSingleMeetingNote: (project_id: string, meetingNote: ProjectMeetingNote) => {
      set((state): any => ({
        ...state,
        projectMeetingNotes: {
          ...state.projectMeetingNotes,
          [project_id]: state.projectMeetingNotes[project_id]?.map((mn: ProjectMeetingNote) =>
            mn.id === meetingNote.id ? { ...mn, ...meetingNote } : mn
          ) || []
        }
      }));
    },
  };
});

export const useProjectsPage = () => {
  const {keycloak: { token }} = useKeycloak();

  return {
    /**
     * Loads the complete project list (every page) into `projects`.
     * The argument is ignored; it is kept so existing `loadProjectsPage(1)` callers keep working.
     */
    loadProjectsPage: async (_page?: number) => {
      if (!token) return;

      const setAllProjects = useProjectsStore.getState().setAllProjects;
      setAllProjects(await getAllProjects());
    },

    /**
     * Loads one server page of projects (with sort and filters) into `listProjects`.
     * @param {ProjectListQuery} query - paging, sort and filter options
     */
    loadProjectsListPage: async (query: ProjectListQuery) => {
      if (!token) return;

      const setListPage = useProjectsStore.getState().setListPage;
      setListPage(await getProjects(token, query));
    },

    refreshProjects: async () => {
      if (!token) return;

      try {
        const setAllProjects = useProjectsStore.getState().setAllProjects;
        setAllProjects(await getAllProjects());
      } catch (error) {
        console.error('Failed to refresh projects:', error);
      }
    },

    loadProjectById: async (id: string) => {
      if (!token) return;

      try {
        console.log('🔄 Loading project by ID:', id);
        
        // Load project data with sites included (thanks to include_sites=true parameter)
        const projectData = await getProjectById(token, id);
        console.log('📦 Project data loaded with sites:', projectData);
        
        // Sites are already included in projectData.sites due to include_sites=true parameter
        // No need for separate API call - just use the project data as is
        console.log('✅ Sites included in main response:', projectData.sites?.length || 0, 'sites');
        
        const setProjectById = useProjectsStore.getState().setProjectById;
        setProjectById(projectData);
      } catch (error: any) {
        console.error('❌ Error loading project:', error);
        
        // Handle 404 errors specifically
        if (error.message && error.message.includes('not found')) {
          console.error('🔍 Project not found, redirecting to 404');
          // You can add navigation to 404 page here if needed
          // navigate('/404');
        }
        
        throw error;
      }
    }
  }
}

// Simple in-memory cache for action items per project (shared across views)
const actionItemsCache: Record<string, { data: any[]; timestamp: number }> = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Run async tasks with a bounded concurrency limit
const fetchWithConcurrency = async <T,>(
  tasks: (() => Promise<T>)[],
  limit: number
): Promise<T[]> => {
  const results: T[] = new Array(tasks.length);
  let index = 0;

  const worker = async () => {
    while (index < tasks.length) {
      const i = index++;
      results[i] = await tasks[i]();
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, () => worker()));
  return results;
};

/**
 * Returns the projects list enriched with each project's `actionItems`, fetched
 * per project (cached, concurrency-limited). Both the map (Dashboard) and the
 * list (Projects) consume this so the risk filter — which matches against
 * `items` and `actionItems` — operates on identical data in both views.
 *
 * Projects are returned immediately (without action items) and re-emitted once
 * enrichment completes.
 */
export const useEnrichedProjects = (source?: any[]): any[] => {
  const { keycloak: { token } } = useKeycloak();
  const storeProjects = useProjectsStore((state) => state.projects);
  const projectsData = source ?? storeProjects;
  const [enrichedProjects, setEnrichedProjects] = useState<any[]>([]);
  const enrichedIdsRef = useRef<string>('');

  const enrichProjects = useCallback(async () => {
    if (!token || !projectsData || projectsData.length === 0) {
      setEnrichedProjects(projectsData || []);
      return;
    }

    // Skip if the project list hasn't changed since the last enrichment
    const projectIds = projectsData.map((p: any) => p.id).sort().join(',');
    if (projectIds === enrichedIdsRef.current && enrichedProjects.length > 0) {
      return;
    }

    // Show projects immediately (without action items), then enrich
    setEnrichedProjects(projectsData);

    const now = Date.now();
    const tasks = projectsData.map((project: any) => async () => {
      const cached = actionItemsCache[project.id];
      if (cached && (now - cached.timestamp) < CACHE_TTL) {
        return { ...project, actionItems: cached.data };
      }
      try {
        // Risk filtering needs every action item, so fetch all pages (not just the first).
        const items = await getAllProjectActionItems(token, project.id);
        actionItemsCache[project.id] = { data: items, timestamp: now };
        return { ...project, actionItems: items };
      } catch {
        return { ...project, actionItems: [] };
      }
    });

    const enriched = await fetchWithConcurrency(tasks, 5); // max 5 concurrent requests
    enrichedIdsRef.current = projectIds;
    setEnrichedProjects(enriched);
  }, [token, projectsData]);

  useEffect(() => {
    enrichProjects();
  }, [enrichProjects]);

  return enrichedProjects;
};

/**
 * Projects matching a risk level, and each project's matching item count, both computed by the backend.
 * Returns empty results when no risk level is selected ('all').
 * @param {string} riskLevel - selected risk level, or 'all'
 */
export const useRiskFilteredProjects = (riskLevel: string): { projects: any[]; counts: Record<string, number> } => {
  const { keycloak: { token } } = useKeycloak();
  const [result, setResult] = useState<{ projects: any[]; counts: Record<string, number> }>({ projects: [], counts: {} });

  useEffect(() => {
    if (!token || riskLevel === 'all') {
      setResult({ projects: [], counts: {} });
      return;
    }
    let cancelled = false;
    Promise.all([getAllProjects({ risk: riskLevel }), getProjectRiskCounts(riskLevel)])
      .then(([projects, counts]) => {
        if (!cancelled) setResult({ projects, counts });
      })
      .catch((error: unknown) => console.error('Failed to load risk-filtered projects:', error));
    return () => {
      cancelled = true;
    };
  }, [token, riskLevel]);

  return result;
};

export const useProjectDailyUpdatePage = (project_id: string) => {
  const { keycloak: { token }} = useKeycloak();

  return {
    loadProjectDailyUpdatesPage: async (_page: number) => {
      if (!token) return;

      const setProjectDailyUpdatePage = useProjectDailyUpdateStore.getState().setProjectDailyUpdatePage;
      setProjectDailyUpdatePage(project_id, await getProjectDailyUpdates(token, project_id));
    },
  }
}

export const useProjectActionItemsPage = (project_id: string) => {
  const { keycloak: { token }} = useKeycloak();

  return {
    /**
     * Loads one server page of a project's action items (with sort and filters).
     * @param {ItemListQuery} query - paging, sort and filter options; defaults to the first page
     */
    loadProjectActionItemsPage: async (query: ItemListQuery = {}) => {
      if (!token) return;

      const setProjectActionItemsPage = useProjectActionItemsStore.getState().setProjectActionItemsPage;
      setProjectActionItemsPage(project_id, await getProjectActionItems(token, project_id, query, { useCache: true }));
    },
  }
}

export const useProjectMeetingNotesPage = (project_id: string) => {
  const { keycloak: { token }} = useKeycloak();

  return {
    loadProjectMeetingNotesPage: async (_page: number) => {
      if (!token) return;

      const setProjectMeetingNotesPage = useProjectMeetingNotesStore.getState().setProjectMeetingNotesPage;
      setProjectMeetingNotesPage(project_id, await getProjectMeetingNotes(token, project_id));
    },
  }
}
